/**
 * Push a marketing-consent change to the server the moment it happens.
 *
 * The browser tags have always honoured a withdrawal immediately. The SERVER
 * paths (Meta and LinkedIn Conversions APIs) read a stored column instead, and
 * that column was only written at profile completion and at checkout. So a
 * signed-in user who opted out in Cookie preferences and then never saved
 * Settings or started a checkout kept a stored `true`, and a later server-side
 * event would transmit against a choice they had already withdrawn.
 *
 * This closes that gap from the client side: whenever the resolved consent
 * value changes, tell the server. The backend method is idempotent and no-ops
 * for anonymous visitors.
 *
 * Deliberately fires ONLY on a change, never on install, so this adds no
 * request to an ordinary page load. A visitor who is not signed in costs
 * nothing either: their choice reaches the server at sign-in, because profile
 * completion already carries the resolved value.
 */
import api from '../api';
import { resolvedConsentForServer, subscribeConsent } from './consentManager';

export function installConsentSyncLifecycle(): () => void {
  // Seeded with the value at boot so the first emit after install is compared
  // against reality rather than treated as a change.
  let lastSent = resolvedConsentForServer()?.granted ?? null;

  return subscribeConsent(() => {
    const resolved = resolvedConsentForServer();
    if (!resolved) return;
    if (resolved.granted === lastSent) return;
    lastSent = resolved.granted;

    // Fire and forget. A failure here must never interrupt the UI — the browser
    // tags have already been stopped or started by their own lifecycles, and
    // the next sign-in or checkout will carry the value again.
    void api.syncMarketingConsent(resolved).catch((err) => {
      console.warn('Could not persist the marketing consent change:', err);
    });
  });
}
